<template>
    <v-col
        class="advanced-billing-work px-4 pt-4"
        cols="12"
      >
        <v-progress-linear
            v-if="loading"
            color="primary"
            indeterminate
        />
        <v-alert
            v-if="errorMessage"
            class="mb-4"
            dense
            outlined
            type="error"
        >
            {{ errorMessage }}
        </v-alert>
        <v-card
            v-if="workflowLoaded"
            class="day-summary mb-4"
            outlined
        >
            <v-card-text class="d-flex align-center py-4">
                <v-avatar
                    class="mr-4"
                    color="primary"
                    size="44"
                >
                    <v-icon dark>
                        mdi-timer-outline
                    </v-icon>
                </v-avatar>
                <div>
                    <div class="day-elapsed">
                        {{ formattedElapsed }}
                    </div>
                    <div class="d-flex align-center flex-wrap">
                        <span class="day-summary-label text-caption text--secondary">
                            Day Summary · {{ displayDate }}
                            <template v-if="displayStartTime">
                                · Started {{ displayStartTime }}
                            </template>
                        </span>
                        <v-chip
                            class="ml-2"
                            color="primary"
                            label
                            outlined
                            x-small
                        >
                            {{ dayStatusLabel }}
                        </v-chip>
                    </div>
                </div>
                <div
                    v-if="demoTimeControlsEnabled"
                    class="timer-adjustments d-flex flex-wrap ml-4"
                >
                    <v-btn
                        v-for="adjustment in timerAdjustments"
                        :key="adjustment.label"
                        class="timer-adjustment"
                        color="primary"
                        outlined
                        rounded
                        x-small
                        @click="adjustDemoTime(adjustment.seconds)"
                    >
                        {{ adjustment.label }}
                    </v-btn>
                    <v-btn
                        class="earliest-rate-start"
                        color="primary"
                        :disabled="earliestRatePeriodStartSeconds === null"
                        outlined
                        rounded
                        x-small
                        @click="setDemoToEarliestRatePeriodStart"
                    >
                        <v-icon
                            left
                            x-small
                        >
                            mdi-clock-start
                        </v-icon>
                        Earliest rate {{ earliestRatePeriodStartLabel }}
                    </v-btn>
                    <v-text-field
                        class="demo-absolute-time ml-2"
                        :class="{
                            'demo-absolute-time--dark': $vuetify.theme.dark,
                        }"
                        dense
                        hide-details
                        label="Simulated time"
                        outlined
                        step="1"
                        type="datetime-local"
                        :value="demoCurrentDateTime"
                        @change="setDemoCurrentDateTime"
                    />
                </div>
                <v-spacer />
                <v-btn
                    v-if="isDevelopment"
                    class="mr-3"
                    color="error"
                    :disabled="resetting"
                    outlined
                    small
                    :loading="resetting"
                    @click="resetAdvancedBilling"
                >
                    <v-icon
                        left
                        small
                    >
                        mdi-restore
                    </v-icon>
                    Reset
                </v-btn>
                <v-tooltip
                    v-if="dayState === 'active'"
                    bottom
                    :disabled="breakModeActive || !hasInProgressJobs"
                >
                    <template #activator="{ on, attrs }">
                        <span
                            class="mr-3"
                            v-bind="attrs"
                            v-on="on"
                        >
                            <v-btn
                                :color="breakModeActive ? 'warning' : 'primary'"
                                :disabled="!breakModeActive && hasInProgressJobs"
                                outlined
                                small
                                @click="breakModeActive ? stopBreak() : startBreak()"
                            >
                                <v-icon
                                    left
                                    small
                                >
                                    {{ breakModeActive
                                        ? 'mdi-coffee-off-outline'
                                        : 'mdi-coffee-outline' }}
                                </v-icon>
                                {{ breakModeActive ? 'End Break' : 'Start Break' }}
                            </v-btn>
                        </span>
                    </template>
                    <span>
                        Complete or cancel the active job before starting a break.
                    </span>
                </v-tooltip>
                <v-btn
                    v-if="dayState === 'not-started'"
                    color="primary"
                    depressed
                    @click="startDay"
                >
                    <v-icon
                        left
                        small
                    >
                        mdi-play
                    </v-icon>
                    Start Day
                </v-btn>
                <v-btn
                    v-else-if="dayState === 'active'"
                    color="primary"
                    :disabled="hasUnprocessedJobs"
                    depressed
                    @click="endDay"
                >
                    End Day
                </v-btn>
                <v-chip
                    v-else-if="dayState === 'submitted'"
                    color="success"
                    label
                    outlined
                >
                    <v-icon
                        left
                        small
                    >
                        mdi-check-circle
                    </v-icon>
                    Submitted
                </v-chip>
            </v-card-text>
            <v-card-text
                v-if="dayTimeline"
                class="day-timeline-section pt-0 pb-4"
            >
                <div class="day-timeline-heading text-caption text--secondary">
                    <span>Day timeline</span>
                    <span>{{ dayTimeline.label }}</span>
                </div>
                <div class="day-timeline">
                    <v-tooltip
                        v-for="block in dayTimelineBlocks"
                        :key="block.key"
                        bottom
                    >
                        <template #activator="{ on, attrs }">
                            <span
                                :class="[
                                    'day-timeline-segment',
                                    {
                                        'day-timeline-segment--transition':
                                            block.type === 'transition',
                                    },
                                ]"
                                :aria-label="block.tooltip"
                                :style="block.style"
                                tabindex="0"
                                v-bind="attrs"
                                v-on="on"
                                @blur="clearTimelineHighlight(block)"
                                @focus="setTimelineHighlight(block)"
                                @mouseenter="setTimelineHighlight(block)"
                                @mouseleave="clearTimelineHighlight(block)"
                            />
                        </template>
                        <span>{{ block.tooltip }}</span>
                    </v-tooltip>
                </div>
                <div
                    v-if="dayTimelineBlocks.length === 0"
                    class="day-timeline-empty text-caption text--secondary"
                >
                    Tracked job and break blocks will appear here.
                </div>
            </v-card-text>
        </v-card>

        <AdvancedBillingTimesheet
            v-if="workflowLoaded && dayState === 'review'"
            :break-timeline-color="breakTimelineColor"
            :breaks="breaks"
            :elapsed-seconds="elapsedSeconds"
            :jobs="jobs"
            :highlighted-row-key="highlightedTimelineKey"
            :submitting="submitting"
            :timeline-colors="jobTimelineColors"
            @submit="submitTimesheet"
        />

        <v-expansion-panels
            v-else-if="workflowLoaded"
            v-model="jobsPanel"
            flat
        >
            <v-expansion-panel>
                <v-expansion-panel-header class="jobs-header px-2">
                    <div class="d-flex align-center">
                        <span class="jobs-title">Today's Jobs</span>
                        <v-chip
                            class="ml-3"
                            color="primary"
                            outlined
                            small
                        >
                            {{ jobs.length }}
                        </v-chip>
                    </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <v-card
                        class="jobs-list"
                        outlined
                    >
                        <AdvancedBillingJob
                            v-for="job in jobs"
                            :key="job.id"
                            :day-active="dayState === 'active'"
                            :job="job"
                            :timeline-color="getJobTimelineColor(job.id)"
                            @arrive="requestJobAction('arrive', $event)"
                            @cancel="cancelJob"
                            @complete="requestJobAction('complete', $event)"
                            @respond="requestJobAction('respond', $event)"
                        />
                        <div
                            v-for="breakEntry in breaks"
                            :key="breakEntry.id"
                            class="break-entry d-flex align-center px-4 py-3"
                            :style="{
                                '--break-timeline-color': breakTimelineColor,
                            }"
                        >
                            <v-avatar
                                class="mr-4"
                                color="primary"
                                size="38"
                            >
                                <v-icon dark>
                                    mdi-coffee-outline
                                </v-icon>
                            </v-avatar>
                            <div>
                                <div class="font-weight-medium">
                                    {{ breakEntry.label || 'Break' }}
                                </div>
                                <div class="text-caption text--secondary">
                                    Non-billable · {{ formatBreakRange(breakEntry) }}
                                </div>
                            </div>
                            <v-spacer />
                            <v-chip
                                :color="breakEntry.endedAt ? 'primary' : 'warning'"
                                label
                                outlined
                                small
                            >
                                {{ formatBreakDuration(breakEntry) }}
                            </v-chip>
                        </div>
                        <v-card-text
                            v-if="jobs.length === 0 && breaks.length === 0"
                            class="text-center text--secondary py-12"
                        >
                            <v-icon
                                class="mb-2"
                                large
                            >
                                mdi-clipboard-text-outline
                            </v-icon>
                            <div>No tickets have been added to today's queue.</div>
                        </v-card-text>
                    </v-card>
                    <div
                        v-if="dayState === 'active' && hasUnprocessedJobs"
                        class="text-caption text--secondary mt-2"
                    >
                        Complete or cancel every job before ending the day.
                    </div>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <section
            v-if="workflowLoaded && submittedTimesheet"
            class="submitted-timesheet mt-6"
        >
            <div class="text-h6 mb-3">
                Timesheet Submitted Today
            </div>
            <v-card
                v-if="submittedDayTimeline"
                class="submitted-day-timeline mb-5"
                outlined
            >
                <v-card-text class="day-timeline-section py-4">
                    <div class="day-timeline-heading text-caption text--secondary">
                        <span>Day timeline</span>
                        <span>{{ submittedDayTimeline.label }}</span>
                    </div>
                    <div class="day-timeline">
                        <v-tooltip
                            v-for="block in submittedDayTimelineBlocks"
                            :key="block.key"
                            bottom
                        >
                            <template #activator="{ on, attrs }">
                                <span
                                    :class="[
                                        'day-timeline-segment',
                                        {
                                            'day-timeline-segment--transition':
                                                block.type === 'transition',
                                        },
                                    ]"
                                    :aria-label="block.tooltip"
                                    :style="block.style"
                                    tabindex="0"
                                    v-bind="attrs"
                                    v-on="on"
                                    @blur="clearTimelineHighlight(block)"
                                    @focus="setTimelineHighlight(block)"
                                    @mouseenter="setTimelineHighlight(block)"
                                    @mouseleave="clearTimelineHighlight(block)"
                                />
                            </template>
                            <span>{{ block.tooltip }}</span>
                        </v-tooltip>
                    </div>
                </v-card-text>
            </v-card>
            <AdvancedBillingTimesheet
                :break-timeline-color="breakTimelineColor"
                :breaks="submittedTimesheet.breaks || []"
                :elapsed-seconds="submittedTimesheet.elapsedSeconds || 0"
                :highlighted-row-key="highlightedTimelineKey"
                :jobs="submittedTimesheet.jobs"
                :timeline-colors="getJobTimelineColors(
                    submittedTimesheet.jobs
                )"
                submitted
            />
        </section>

        <v-dialog
            v-model="breakActionDialog"
            max-width="520"
            persistent
        >
            <v-card>
                <v-card-title>
                    You are currently on break
                </v-card-title>
                <v-card-text v-if="breakActionStep === 'confirm'">
                    If you forgot to toggle the break button, you can fix that
                    now. Did you mean to stop your break before now?
                </v-card-text>
                <template v-else>
                    <v-card-text>
                        How long was your break?
                        <v-radio-group v-model="selectedBreakDurationMinutes">
                            <v-radio
                                v-for="duration in breakDurationOptions"
                                :key="duration.minutes"
                                :label="duration.label"
                                :value="duration.minutes"
                            />
                        </v-radio-group>
                    </v-card-text>
                </template>
                <v-card-actions>
                    <v-btn
                        text
                        @click="cancelPendingBreakAction"
                    >
                        Cancel
                    </v-btn>
                    <v-spacer />
                    <template v-if="breakActionStep === 'confirm'">
                        <v-btn
                            text
                            @click="breakActionStep = 'duration'"
                        >
                            Yes, adjust it
                        </v-btn>
                        <v-btn
                            color="primary"
                            depressed
                            @click="resolveBreakActionNow"
                        >
                            No, end break now
                        </v-btn>
                    </template>
                    <v-btn
                        v-else
                        color="primary"
                        :disabled="!selectedBreakDurationMinutes"
                        depressed
                        @click="resolveBreakActionWithDuration"
                    >
                        Continue
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-col>
</template>

<script>
import AdvancedBillingJob from './AdvancedBillingJob.vue';
import AdvancedBillingTimesheet from './AdvancedBillingTimesheet.vue';
import { advancedBillingApi } from '@/services/advancedBillingApi';
import { createAdvancedBillingTimeProvider } from '@/services/advancedBillingTime';
import {
    calculateJobBilling,
    normalizeRatePeriods,
    parseTimeOfDay,
} from '@/services/advancedBillingCalculations';

const JOB_TIMELINE_COLORS = [
    '#42A5F5',
    '#AB47BC',
    '#26A69A',
    '#FFA726',
    '#EC407A',
    '#7E57C2',
];
const BREAK_TIMELINE_COLOR = '#8D6E63';
const TRANSITION_TIMELINE_COLOR = 'rgba(128, 128, 128, .24)';

export default {
    name: 'BillingWork',
    components: {
        AdvancedBillingJob,
        AdvancedBillingTimesheet,
    },
    props: {
        api: {
            type: Object,
            default: () => advancedBillingApi,
        },
        timeProvider: {
            type: Object,
            default: () => createAdvancedBillingTimeProvider(),
        },
    },
    data: () => ({
        dayState: 'not-started',
        breakActionDialog: false,
        breakActionStep: 'confirm',
        breakDurationOptions: [
            { label: '15 minutes or less', minutes: 15 },
            { label: '30 minutes', minutes: 30 },
            { label: '45 minutes', minutes: 45 },
            { label: '1 hour', minutes: 60 },
        ],
        breakModeActive: false,
        breakTimelineColor: BREAK_TIMELINE_COLOR,
        breaks: [],
        clockNow: null,
        elapsedSeconds: 0,
        errorMessage: '',
        jobs: [],
        jobsPanel: 0,
        highlightedTimelineKey: null,
        loading: true,
        pendingBreakAction: null,
        resetting: false,
        saveQueue: Promise.resolve(),
        selectedBreakDurationMinutes: null,
        submittedTimesheet: null,
        submitting: false,
        timerAdjustments: [
            { label: '+1m', seconds: 60 },
            { label: '+30m', seconds: 1800 },
            { label: '+1h', seconds: 3600 },
            { label: '-1h', seconds: -3600 },
            { label: '-30m', seconds: -1800 },
            { label: '-1m', seconds: -60 },
        ],
        timerId: null,
        workflow: {},
        workflowLoaded: false,
    }),
    computed: {
        configuredRatePeriods() {
            return this.$store?.getters?.companyBillingRatePeriods || [];
        },
        jobTimelineColors() {
            return this.getJobTimelineColors(this.jobs);
        },
        dayTimeline() {
            return this.buildDayTimeline(
                this.workflow,
                this.jobs,
                this.breaks,
                this.dayState === 'active'
            );
        },
        dayTimelineBlocks() {
            return this.buildDayTimelineBlocks(
                this.dayTimeline,
                this.jobs,
                this.breaks
            );
        },
        submittedDayTimeline() {
            if(!this.submittedTimesheet) {
                return null;
            }
            return this.buildDayTimeline(
                this.submittedTimesheet,
                this.submittedTimesheet.jobs || [],
                this.submittedTimesheet.breaks || [],
                false
            );
        },
        submittedDayTimelineBlocks() {
            if(!this.submittedTimesheet) {
                return [];
            }
            return this.buildDayTimelineBlocks(
                this.submittedDayTimeline,
                this.submittedTimesheet.jobs || [],
                this.submittedTimesheet.breaks || []
            );
        },
        demoTimeControlsEnabled() {
            return Boolean(this.timeProvider.adjustable);
        },
        demoCurrentDateTime() {
            const currentDate = this.clockNow || this.getCurrentDate();
            const localDate = new Date(
                currentDate.getTime()
                    - (currentDate.getTimezoneOffset() * 60000)
            );
            return localDate.toISOString().slice(0, 19);
        },
        earliestRatePeriodStartLabel() {
            if(this.earliestRatePeriodStartSeconds === null) {
                return '—';
            }
            const hours = Math.floor(
                this.earliestRatePeriodStartSeconds / 3600
            );
            const minutes = Math.floor(
                (this.earliestRatePeriodStartSeconds % 3600) / 60
            );
            return `${String(hours).padStart(2, '0')}:`
                + `${String(minutes).padStart(2, '0')}`;
        },
        earliestRatePeriodStartSeconds() {
            const configuredRatePeriods = Array.isArray(
                this.configuredRatePeriods
            )
                ? this.configuredRatePeriods
                : this.configuredRatePeriods?.rate_periods || [];
            const startTimes = configuredRatePeriods
                .map(ratePeriod => Number.isFinite(ratePeriod.startSeconds)
                    ? ratePeriod.startSeconds
                    : parseTimeOfDay(
                        ratePeriod.time_from || ratePeriod.startTime
                    ))
                .filter(Number.isFinite);
            return startTimes.length ? Math.min(...startTimes) : null;
        },
        dayStatusLabel() {
            return {
                'not-started': 'Not Started',
                active: 'In Progress',
                review: 'Ready to Submit',
                submitted: 'Submitted',
            }[this.dayState];
        },
        displayDate() {
            return new Intl.DateTimeFormat('en-US', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            }).format(this.clockNow || this.getCurrentDate());
        },
        displayStartTime() {
            if(this.dayState !== 'active' || !this.workflow.startedAt) {
                return '';
            }
            const startedAt = new Date(this.workflow.startedAt);
            if(!Number.isFinite(startedAt.getTime())) {
                return '';
            }
            return new Intl.DateTimeFormat('en-US', {
                hour: 'numeric',
                minute: '2-digit',
            }).format(startedAt);
        },
        formattedElapsed() {
            const hours = Math.floor(this.elapsedSeconds / 3600);
            const minutes = Math.floor((this.elapsedSeconds % 3600) / 60);
            const seconds = this.elapsedSeconds % 60;
            return [hours, minutes, seconds]
                .map(value => String(value).padStart(2, '0'))
                .join(':');
        },
        hasUnprocessedJobs() {
            return this.breakModeActive
                || this.jobs.some(job => (
                    ['pending', 'in-progress'].includes(job.status)
                ));
        },
        hasInProgressJobs() {
            return this.jobs.some(job => job.status === 'in-progress');
        },
        openBreak() {
            return this.breaks.find(breakEntry => !breakEntry.endedAt) || null;
        },
        isDevelopment() {
            return process.env.NODE_ENV !== 'production';
        },
    },
    created() {
        this.loadWorkflow();
    },
    beforeDestroy() {
        this.stopTimer();
        if(this.workflowLoaded) {
            this.persistWorkflow();
        }
    },
    methods: {
        async loadWorkflow() {
            this.loading = true;
            this.errorMessage = '';
            this.workflowLoaded = false;
            try {
                const workflow = await this.api.getWorkflow();
                this.syncClockNow();
                this.workflow = this.normalizeLoadedWorkflow(workflow);
                this.restoreAdjustableClock(this.workflow);
                this.syncClockNow();
                this.dayState = this.workflow.dayState;
                this.breakModeActive = Boolean(this.workflow.breakModeActive);
                this.breaks = Array.isArray(this.workflow.breaks)
                    ? this.workflow.breaks
                    : [];
                this.elapsedSeconds = this.getPersistedElapsedSeconds(
                    this.workflow
                );
                this.jobs = this.workflow.jobs.map(job => {
                    const normalizedJob = {
                        ...job,
                        billingStartedAt: job.billingStartedAt
                            ? new Date(job.billingStartedAt)
                            : null,
                        arrivedAt: job.arrivedAt
                            ? new Date(job.arrivedAt)
                            : null,
                        completedAt: job.completedAt
                            ? new Date(job.completedAt)
                            : null,
                    };
                    return normalizedJob.arrivedAt
                        && normalizedJob.status !== 'cancelled'
                        ? calculateJobBilling(
                            normalizedJob,
                            this.configuredRatePeriods,
                            this.getCurrentDate()
                        )
                        : normalizedJob;
                });
                this.submittedTimesheet = this.workflow.submittedTimesheet || null;
                if(this.dayState === 'active') {
                    this.startTimer();
                }
                this.workflowLoaded = true;
                this.$emit('workflow-change', this.getWorkflowSnapshot());
                if(this.workflow !== workflow) {
                    await this.persistWorkflow();
                }
            } catch(error) {
                this.errorMessage = error.message;
            } finally {
                this.loading = false;
            }
        },
        getWorkflowSnapshot() {
            return {
                ...this.workflow,
                dayState: this.dayState,
                breakModeActive: this.breakModeActive,
                breaks: this.breaks,
                elapsedSeconds: this.elapsedSeconds,
                jobs: this.jobs,
                submittedTimesheet: this.submittedTimesheet,
            };
        },
        persistWorkflow() {
            const workflow = this.getWorkflowSnapshot();
            this.workflow = workflow;
            this.$emit('workflow-change', workflow);
            this.saveQueue = this.saveQueue
                .then(() => this.api.saveWorkflow(workflow))
                .then(savedWorkflow => {
                    this.workflow = savedWorkflow;
                    this.$emit('workflow-change', savedWorkflow);
                    return savedWorkflow;
                })
                .catch(error => {
                    this.errorMessage = error.message;
                });
            return this.saveQueue;
        },
        startDay() {
            this.workflow = {
                ...this.workflow,
                endedAt: null,
                startedAt: this.getCurrentDate().toISOString(),
            };
            this.elapsedSeconds = 0;
            this.breakModeActive = false;
            this.breaks = [];
            this.dayState = 'active';
            this.startTimer();
            this.persistWorkflow();
            this.$emit('start-day');
        },
        endDay() {
            if(this.hasUnprocessedJobs) {
                return;
            }
            this.reconcileElapsedSeconds();
            this.workflow = {
                ...this.workflow,
                endedAt: this.getCurrentDate().toISOString(),
            };
            this.dayState = 'review';
            this.stopTimer();
            this.persistWorkflow();
            this.$emit('end-day', { elapsedSeconds: this.elapsedSeconds });
        },
        async submitTimesheet() {
            if(this.submitting || this.dayState !== 'review') {
                return;
            }

            this.submitting = true;
            this.errorMessage = '';
            try {
                await this.saveQueue;
                const submittedAt = this.getCurrentDate().toISOString();
                const submission = {
                    ...this.getWorkflowSnapshot(),
                    dayState: 'submitted',
                    submittedAt,
                    submittedTimesheet: undefined,
                };
                const savedSubmission = await this.api.submitWorkflow(submission);
                this.submittedTimesheet = {
                    ...savedSubmission,
                    dayState: 'submitted',
                    submittedAt: savedSubmission.submittedAt || submittedAt,
                };
                this.workflow = {
                    workDate: submission.workDate,
                    dayState: 'not-started',
                    elapsedSeconds: 0,
                    endedAt: null,
                    startedAt: null,
                    breakModeActive: false,
                    breaks: [],
                    jobs: [],
                    submittedTimesheet: this.submittedTimesheet,
                };
                this.dayState = 'not-started';
                this.elapsedSeconds = 0;
                this.breakModeActive = false;
                this.breaks = [];
                this.jobs = [];
                await this.persistWorkflow();
                this.$emit('submit-timesheet', this.submittedTimesheet);
            } catch(error) {
                this.errorMessage = error.message;
            } finally {
                this.submitting = false;
            }
        },
        async resetAdvancedBilling() {
            if(this.resetting) {
                return;
            }

            this.resetting = true;
            this.errorMessage = '';
            this.stopTimer();
            try {
                await this.saveQueue;
                const workflow = await this.api.resetWorkflow(
                    this.workflow.workDate
                );
                this.workflow = workflow;
                this.dayState = workflow.dayState;
                this.breakModeActive = Boolean(workflow.breakModeActive);
                this.breaks = Array.isArray(workflow.breaks)
                    ? workflow.breaks
                    : [];
                this.elapsedSeconds = workflow.elapsedSeconds;
                this.jobs = workflow.jobs;
                this.submittedTimesheet = null;
                this.$emit('workflow-change', workflow);
            } catch(error) {
                this.errorMessage = error.message;
                if(this.dayState === 'active') {
                    this.startTimer();
                }
            } finally {
                this.resetting = false;
            }
        },
        startTimer() {
            if(this.timerId) {
                return;
            }
            this.reconcileElapsedSeconds();
            this.timerId = window.setInterval(() => {
                if(this.demoTimeControlsEnabled) {
                    this.timeProvider.advance(1);
                }
                this.syncClockNow();
                this.reconcileElapsedSeconds();
                this.refreshInProgressBilling();
            }, 1000);
        },
        stopTimer() {
            if(!this.timerId) {
                return;
            }
            window.clearInterval(this.timerId);
            this.timerId = null;
        },
        normalizeActiveWorkflow(workflow) {
            if(workflow.dayState !== 'active'
                || this.getStartedAtMilliseconds(workflow.startedAt) !== null) {
                return workflow;
            }

            const elapsedSeconds = Number.isFinite(workflow.elapsedSeconds)
                ? Math.max(0, workflow.elapsedSeconds)
                : 0;
            return {
                ...workflow,
                startedAt: new Date(
                    this.getCurrentDate().getTime()
                        - (elapsedSeconds * 1000)
                ).toISOString(),
            };
        },
        asValidDate(value) {
            if(!value) {
                return null;
            }
            const date = new Date(value);
            return Number.isFinite(date.getTime()) ? date : null;
        },
        buildDayTimeline(workflow, jobs, breaks, includeCurrentTime) {
            const startedAt = this.asValidDate(workflow?.startedAt);
            if(!startedAt) {
                return null;
            }
            const capturedEnds = [
                workflow.endedAt,
                ...jobs.map(job => job.completedAt),
                ...breaks.map(breakEntry => breakEntry.endedAt),
            ].map(this.asValidDate).filter(Boolean);
            if(includeCurrentTime) {
                capturedEnds.push(this.clockNow || this.getCurrentDate());
            }
            const endedAt = capturedEnds.length
                ? new Date(Math.max(
                    startedAt.getTime(),
                    ...capturedEnds.map(date => date.getTime())
                ))
                : startedAt;
            return {
                startedAt,
                endedAt,
                durationMilliseconds: Math.max(
                    1,
                    endedAt.getTime() - startedAt.getTime()
                ),
                label: `${this.formatTimelineTime(startedAt)}–`
                    + `${this.formatTimelineTime(endedAt)}`,
            };
        },
        buildDayTimelineBlocks(timeline, jobs, breaks) {
            if(!timeline) {
                return [];
            }
            const trackedBlocks = [];
            jobs.forEach(job => {
                const startedAt = this.asValidDate(
                    job.billingStartedAt || job.arrivedAt
                );
                const arrivedAt = this.asValidDate(job.arrivedAt);
                const endedAt = this.asValidDate(job.completedAt)
                    || (job.status === 'in-progress'
                        ? timeline.endedAt
                        : null);
                if(startedAt && endedAt) {
                    trackedBlocks.push(this.createTimelineBlock({
                        key: `job-${job.id}`,
                        label: `Job ${job.ticketNumber || job.id}`
                            + (arrivedAt && startedAt < arrivedAt
                                ? ' (includes travel)'
                                : ''),
                        startedAt,
                        endedAt,
                        color: this.getJobTimelineColor(job.id, jobs),
                        highlightKey: `job:${job.id}`,
                    }, timeline));
                }
            });
            breaks.forEach(breakEntry => {
                const startedAt = this.asValidDate(breakEntry.startedAt);
                const endedAt = this.asValidDate(breakEntry.endedAt)
                    || timeline.endedAt;
                if(startedAt && endedAt) {
                    trackedBlocks.push(this.createTimelineBlock({
                        key: `break-${breakEntry.id}`,
                        label: breakEntry.label || 'Break',
                        startedAt,
                        endedAt,
                        color: this.breakTimelineColor,
                        highlightKey: `break:${breakEntry.id}`,
                    }, timeline));
                }
            });
            const blocks = trackedBlocks
                .filter(Boolean)
                .sort((left, right) => left.startedAt - right.startedAt);
            const transitionBlocks = [];
            let coveredUntil = timeline.startedAt.getTime();
            blocks.forEach(block => {
                const blockStart = block.startedAt.getTime();
                const blockEnd = block.endedAt.getTime();
                if(blockStart > coveredUntil) {
                    transitionBlocks.push(this.createTimelineBlock({
                        key: `transition-${coveredUntil}`,
                        label: 'Transition time',
                        startedAt: new Date(coveredUntil),
                        endedAt: new Date(blockStart),
                        color: TRANSITION_TIMELINE_COLOR,
                        type: 'transition',
                    }, timeline));
                }
                coveredUntil = Math.max(coveredUntil, blockEnd);
            });
            const timelineEnd = timeline.endedAt.getTime();
            if(coveredUntil < timelineEnd) {
                transitionBlocks.push(this.createTimelineBlock({
                    key: `transition-${coveredUntil}`,
                    label: 'Transition time',
                    startedAt: new Date(coveredUntil),
                    endedAt: new Date(timelineEnd),
                    color: TRANSITION_TIMELINE_COLOR,
                    type: 'transition',
                }, timeline));
            }
            return [...blocks, ...transitionBlocks]
                .filter(Boolean)
                .sort((left, right) => left.startedAt - right.startedAt);
        },
        createTimelineBlock({
            key,
            label,
            startedAt,
            endedAt,
            color,
            type = 'tracked',
            highlightKey = null,
        }, timeline = this.dayTimeline) {
            const timelineStart = timeline.startedAt.getTime();
            const timelineEnd = timeline.endedAt.getTime();
            const blockStart = Math.max(startedAt.getTime(), timelineStart);
            const blockEnd = Math.min(
                Math.max(endedAt.getTime(), blockStart),
                timelineEnd
            );
            if(blockStart > timelineEnd) {
                return null;
            }
            const left = (
                (blockStart - timelineStart)
                / timeline.durationMilliseconds
            ) * 100;
            const width = (
                (blockEnd - blockStart)
                / timeline.durationMilliseconds
            ) * 100;
            const durationSeconds = Math.max(
                0,
                Math.round((blockEnd - blockStart) / 1000)
            );
            return {
                key,
                label,
                startedAt: new Date(blockStart),
                endedAt: new Date(blockEnd),
                color,
                type,
                highlightKey,
                style: {
                    backgroundColor: color,
                    left: `${left}%`,
                    width: `max(${width}%, 4px)`,
                },
                tooltip: `${label} · ${this.formatTimelineTime(blockStart)}–`
                    + `${this.formatTimelineTime(blockEnd)} · `
                    + `${this.formatTimelineDuration(durationSeconds)}`,
            };
        },
        formatTimelineDuration(seconds) {
            if(seconds > 0 && seconds < 60) {
                return `${seconds}s`;
            }
            const totalMinutes = Math.max(0, Math.round(seconds / 60));
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            if(hours && minutes) {
                return `${hours}h ${minutes}m`;
            }
            if(hours) {
                return `${hours}h`;
            }
            return `${minutes}m`;
        },
        formatTimelineTime(value) {
            return new Intl.DateTimeFormat('en-US', {
                hour: 'numeric',
                minute: '2-digit',
            }).format(new Date(value));
        },
        getJobTimelineColor(jobId, jobs = this.jobs) {
            const jobIndex = jobs.findIndex(job => job.id === jobId);
            return JOB_TIMELINE_COLORS[
                Math.max(0, jobIndex) % JOB_TIMELINE_COLORS.length
            ];
        },
        getJobTimelineColors(jobs = []) {
            return jobs.reduce((colors, job) => ({
                ...colors,
                [job.id]: this.getJobTimelineColor(job.id, jobs),
            }), {});
        },
        setTimelineHighlight(block) {
            this.highlightedTimelineKey = block.highlightKey || null;
        },
        clearTimelineHighlight(block) {
            if(this.highlightedTimelineKey === block.highlightKey) {
                this.highlightedTimelineKey = null;
            }
        },
        getCurrentDate() {
            return this.timeProvider.now();
        },
        syncClockNow() {
            this.clockNow = this.getCurrentDate();
        },
        normalizeLoadedWorkflow(workflow) {
            if(Object.prototype.hasOwnProperty.call(
                workflow,
                'demoTimeOffsetSeconds'
            )) {
                workflow = { ...workflow };
                delete workflow.demoTimeOffsetSeconds;
            }
            if(workflow.dayState === 'submitted') {
                const submittedTimesheet = {
                    ...workflow,
                    submittedTimesheet: undefined,
                };
                return {
                    workDate: workflow.workDate,
                    dayState: 'not-started',
                    elapsedSeconds: 0,
                    endedAt: null,
                    startedAt: null,
                    breakModeActive: false,
                    breaks: [],
                    jobs: [],
                    submittedTimesheet,
                };
            }
            return this.normalizeActiveWorkflow(workflow);
        },
        getStartedAtMilliseconds(startedAt) {
            if(typeof startedAt !== 'string' || !startedAt) {
                return null;
            }
            const milliseconds = Date.parse(startedAt);
            return Number.isFinite(milliseconds) ? milliseconds : null;
        },
        getPersistedElapsedSeconds(workflow) {
            const persistedElapsedSeconds = Number.isFinite(
                workflow.elapsedSeconds
            )
                ? Math.max(0, workflow.elapsedSeconds)
                : 0;
            const startedAtMilliseconds = this.getStartedAtMilliseconds(
                workflow.startedAt
            );
            if(workflow.dayState !== 'active'
                || startedAtMilliseconds === null) {
                return persistedElapsedSeconds;
            }
            return Math.max(
                0,
                Math.floor(
                    (this.getCurrentDate().getTime()
                        - startedAtMilliseconds)
                    / 1000
                )
            );
        },
        reconcileElapsedSeconds() {
            this.elapsedSeconds = this.getPersistedElapsedSeconds({
                ...this.workflow,
                dayState: this.dayState,
                elapsedSeconds: this.elapsedSeconds,
            });
        },
        restoreAdjustableClock(workflow) {
            if(!this.timeProvider.adjustable
                || workflow.dayState !== 'active') {
                return;
            }
            const startedAtMilliseconds = this.getStartedAtMilliseconds(
                workflow.startedAt
            );
            if(startedAtMilliseconds === null) {
                return;
            }
            const elapsedSeconds = Number.isFinite(workflow.elapsedSeconds)
                ? Math.max(0, workflow.elapsedSeconds)
                : 0;
            this.timeProvider.set(new Date(
                startedAtMilliseconds + (elapsedSeconds * 1000)
            ));
        },
        adjustDemoTime(seconds) {
            if(!this.demoTimeControlsEnabled) {
                return;
            }
            this.timeProvider.advance(seconds);
            this.syncClockNow();
            this.applyDemoTimeChange();
        },
        setDemoCurrentDateTime(value) {
            if(!this.demoTimeControlsEnabled) {
                return;
            }
            const targetDate = new Date(value);
            if(!Number.isFinite(targetDate.getTime())) {
                return;
            }
            this.timeProvider.set(targetDate);
            this.syncClockNow();
            this.applyDemoTimeChange();
        },
        setDemoToEarliestRatePeriodStart() {
            const startSeconds = this.earliestRatePeriodStartSeconds;
            if(!this.demoTimeControlsEnabled || startSeconds === null) {
                return;
            }
            const targetDate = this.getCurrentDate();
            targetDate.setHours(
                Math.floor(startSeconds / 3600),
                Math.floor((startSeconds % 3600) / 60),
                startSeconds % 60,
                0
            );
            this.timeProvider.set(targetDate);
            this.syncClockNow();
            this.applyDemoTimeChange();
        },
        applyDemoTimeChange() {
            const now = this.getCurrentDate();
            const startedAt = this.getStartedAtMilliseconds(
                this.workflow.startedAt
            );
            this.elapsedSeconds = startedAt === null
                ? 0
                : Math.max(0, Math.floor(
                    (now.getTime() - startedAt) / 1000
                ));
            this.workflow = {
                ...this.workflow,
                elapsedSeconds: this.elapsedSeconds,
            };
            this.refreshInProgressBilling();
            this.persistWorkflow();
        },
        arriveAtJob(jobId) {
            const now = this.getCurrentDate();
            if(this.breakModeActive) {
                this.closeOpenBreakSegment(now);
            }
            const billingStartedAt = this.getJobBillingStart(now);
            const ratePeriods = normalizeRatePeriods(
                this.configuredRatePeriods
            );
            this.updateJob(jobId, job => calculateJobBilling({
                ...job,
                status: 'in-progress',
                billingStartedAt,
                arrivedAt: now,
                completedAt: null,
                ratePeriods,
            }, ratePeriods, now));
            this.persistWorkflow();
            this.$emit('arrive', jobId);
        },
        getJobBillingStart(arrivedAt) {
            const arrivalTime = arrivedAt.getTime();
            const candidates = [this.workflow.startedAt]
                .concat(this.jobs
                    .filter(job => job.status === 'completed')
                    .map(job => job.completedAt))
                .concat(this.breaks.map(breakEntry => breakEntry.endedAt))
                .map(this.asValidDate)
                .filter(date => date && date.getTime() <= arrivalTime);
            if(!candidates.length) {
                return arrivedAt;
            }
            return new Date(Math.max(
                ...candidates.map(date => date.getTime())
            ));
        },
        completeJob(jobId) {
            const jobToComplete = this.jobs.find(job => job.id === jobId);
            if(jobToComplete?.status !== 'in-progress'
                || !jobToComplete.respondedAt) {
                return;
            }
            const completedAt = this.getCurrentDate();
            this.updateJob(jobId, job => calculateJobBilling({
                ...job,
                status: 'completed',
                completedAt,
            }, this.configuredRatePeriods, completedAt));
            this.resumeBreakIfEligible(completedAt);
            this.persistWorkflow();
            this.$emit('complete', jobId);
        },
        cancelJob(jobId) {
            if(this.dayState === 'not-started') {
                this.jobs = this.jobs.filter(job => job.id !== jobId);
                this.persistWorkflow();
                this.$emit('cancel', jobId);
                return;
            }
            const completedAt = this.getCurrentDate();
            this.updateJob(jobId, {
                status: 'cancelled',
                completedAt,
                trackedSeconds: 0,
                units: 0,
                allocations: [],
            });
            this.resumeBreakIfEligible(completedAt);
            this.persistWorkflow();
            this.$emit('cancel', jobId);
        },
        refreshInProgressBilling() {
            const now = this.getCurrentDate();
            this.jobs = this.jobs.map(job => (
                job.status === 'in-progress'
                    ? calculateJobBilling(
                        job,
                        this.configuredRatePeriods,
                        now
                    )
                    : job
            ));
        },
        respondToJob(job) {
            this.$emit('respond', {
                ...job,
                responseOpenedAt: this.getCurrentDate().toISOString(),
            });
        },
        requestJobAction(action, payload) {
            if(!this.openBreak) {
                this.performJobAction(action, payload);
                return;
            }
            this.pendingBreakAction = { action, payload };
            this.breakActionStep = 'confirm';
            this.selectedBreakDurationMinutes = null;
            this.breakActionDialog = true;
        },
        cancelPendingBreakAction() {
            this.breakActionDialog = false;
            this.pendingBreakAction = null;
            this.breakActionStep = 'confirm';
            this.selectedBreakDurationMinutes = null;
        },
        resolveBreakActionNow() {
            this.closeOpenBreakSegment(this.getCurrentDate());
            this.continuePendingBreakAction();
        },
        resolveBreakActionWithDuration() {
            if(!this.openBreak || !this.selectedBreakDurationMinutes) {
                return;
            }
            const now = this.getCurrentDate();
            const startedAt = new Date(this.openBreak.startedAt);
            const intendedEnd = new Date(
                startedAt.getTime()
                    + (this.selectedBreakDurationMinutes * 60 * 1000)
            );
            this.closeOpenBreakSegment(
                intendedEnd.getTime() < now.getTime() ? intendedEnd : now
            );
            this.continuePendingBreakAction();
        },
        continuePendingBreakAction() {
            const pendingAction = this.pendingBreakAction;
            this.cancelPendingBreakAction();
            if(!pendingAction) {
                return;
            }
            this.performJobAction(
                pendingAction.action,
                pendingAction.payload
            );
            if(pendingAction.action === 'respond') {
                this.persistWorkflow();
            }
        },
        performJobAction(action, payload) {
            ({
                arrive: this.arriveAtJob,
                complete: this.completeJob,
                respond: this.respondToJob,
            }[action])?.(payload);
        },
        startBreak() {
            if(this.dayState !== 'active'
                || this.breakModeActive
                || this.hasInProgressJobs) {
                return;
            }
            this.breakModeActive = true;
            this.openBreakSegment(this.getCurrentDate());
            this.persistWorkflow();
        },
        stopBreak() {
            if(!this.breakModeActive) {
                return;
            }
            this.closeOpenBreakSegment(this.getCurrentDate());
            this.breakModeActive = false;
            this.persistWorkflow();
        },
        openBreakSegment(startedAt) {
            if(this.openBreak || this.hasInProgressJobs) {
                return;
            }
            this.breaks = [
                ...this.breaks,
                {
                    id: `break-${startedAt.getTime()}-${this.breaks.length}`,
                    label: 'Break',
                    startedAt: startedAt.toISOString(),
                    endedAt: null,
                },
            ];
        },
        closeOpenBreakSegment(endedAt) {
            if(!this.openBreak) {
                return;
            }
            const openBreakId = this.openBreak.id;
            this.breaks = this.breaks.map(breakEntry => (
                breakEntry.id === openBreakId
                    ? { ...breakEntry, endedAt: endedAt.toISOString() }
                    : breakEntry
            ));
        },
        resumeBreakIfEligible(startedAt) {
            if(this.breakModeActive && !this.hasInProgressJobs) {
                this.openBreakSegment(startedAt);
            }
        },
        formatBreakRange(breakEntry) {
            const format = value => new Intl.DateTimeFormat('en-US', {
                hour: 'numeric',
                minute: '2-digit',
            }).format(new Date(value));
            return `${format(breakEntry.startedAt)} – ${
                breakEntry.endedAt ? format(breakEntry.endedAt) : 'In progress'
            }`;
        },
        formatBreakDuration(breakEntry) {
            const endedAt = breakEntry.endedAt
                ? new Date(breakEntry.endedAt)
                : this.getCurrentDate();
            const seconds = Math.max(0, Math.floor(
                (endedAt.getTime() - new Date(breakEntry.startedAt).getTime())
                / 1000
            ));
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return hours ? `${hours}h ${minutes}m` : `${minutes}m`;
        },
        updateJob(jobId, updates) {
            this.jobs = this.jobs.map(job => (
                job.id === jobId
                    ? typeof updates === 'function'
                        ? updates(job)
                        : { ...job, ...updates }
                    : job
            ));
        },
    },
};
</script>

<style scoped>
.advanced-billing-work {
    min-width: 0;
}

.day-summary {
    border-radius: 8px;
}

.day-timeline-section {
    margin-left: 60px;
    max-width: 780px;
}

.submitted-day-timeline .day-timeline-section {
    margin-left: 0;
}

.day-timeline-heading {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
    width: 720px;
    max-width: 100%;
}

.day-timeline {
    background: rgba(128, 128, 128, .24);
    border-radius: 4px;
    height: 8px;
    overflow: hidden;
    position: relative;
    width: 720px;
    max-width: 100%;
}

.day-timeline-segment {
    border-left: 1px solid rgba(255, 255, 255, .72);
    bottom: 0;
    cursor: help;
    position: absolute;
    top: 0;
}

.day-timeline-segment--transition {
    border-left: 0;
}

.day-timeline-segment--transition:hover,
.day-timeline-segment--transition:focus {
    background-color: rgba(128, 128, 128, .42) !important;
}

.day-timeline-empty {
    margin-top: 4px;
}

.day-elapsed {
    font-size: 1.75rem;
    font-weight: 500;
    letter-spacing: .02em;
    line-height: 1.2;
}

.timer-adjustments {
    gap: 4px;
}

.timer-adjustment {
    min-width: 0 !important;
    padding: 0 8px !important;
}

.demo-absolute-time {
    max-width: 210px;
}

.demo-absolute-time--dark /deep/ input {
    color-scheme: dark;
}

.jobs-header {
    min-height: 48px;
}

.jobs-title {
    font-size: 1rem;
    font-weight: 500;
    line-height: 28px;
}

.jobs-list {
    border-radius: 8px;
    overflow: hidden;
}

.break-entry {
    border-top: 1px solid rgba(128, 128, 128, .24);
    border-left: 4px solid var(--break-timeline-color);
}

@media (max-width: 600px) {
    .day-timeline-section {
        margin-left: 0;
    }
}
</style>
